import {
  AdminUser,
  Application,
  ApplicationAssignmentPayload,
  ApplicationModule,
  CreateApplicationPayload,
  CreateApplicationModulePayload,
  AuthResponse,
  ChangePasswordPayload,
  CreateAdminUserPayload,
  CreateDealerPayload,
  UpdateApplicationPayload,
  UpdateApplicationModulePayload,
  CreateTenantPayload,
  Dealer,
  LoginPayload,
  ModuleAssignmentPayload,
  DashboardMetrics,
  BusinessMetrics,
  MasterDashboard,
  Tenant,
  UpdateAdminUserPayload,
  UpdateDealerPayload,
  UpdateTenantPayload,
  UserAccount,
  Device,
  DeviceStats,
  SystemHealth,

  SupportTicket,
  TicketMessage,
  CreateTicketPayload,
  UpdateTicketPayload,
  TicketStats,
  FaqItem,
  CreateFaqPayload,
  UpdateFaqPayload,
  Group,
  CreateGroupPayload,
  UpdateGroupPayload,
  Notification,
} from '../types';

const DEFAULT_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5194';

type TenantIdProvider = () => string | null;

let tenantIdProvider: TenantIdProvider = () => null;

export function setTenantIdProvider(provider: TenantIdProvider | null | undefined) {
  tenantIdProvider = provider ?? (() => null);
}

export class ApiError extends Error {
  code?: string;
  details?: any;
  status?: number;

  constructor(message: string, code?: string, details?: any, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.status = status;
  }
}

interface RequestOptions extends RequestInit {
  token?: string | null;
  tenantId?: string | null;
  skipTenantHeader?: boolean;
  skipAuthHandling?: boolean;
  _retryCount?: number;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { token, tenantId, skipTenantHeader, headers: initialHeaders, ...rest } = options;
  const headers = new Headers(initialHeaders ?? {});
  const isJson = rest.body && !(rest.body instanceof FormData);
  if (isJson) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (!skipTenantHeader) {
    const resolvedTenantId = tenantId ?? tenantIdProvider();
    if (resolvedTenantId) {
      headers.set('X-Tenant-Id', resolvedTenantId);
    }
  }

  // Add Language Header for I18n
  const currentLang = typeof navigator !== 'undefined' ? navigator.language : 'pt-BR';
  headers.set('Accept-Language', currentLang);

  const response = await fetch(`${DEFAULT_BASE_URL}${path}`, {
    ...rest,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let details: unknown;
    // Read body as text first to avoid "body stream already read" error
    const bodyText = await response.text();
    try {
      details = JSON.parse(bodyText);
    } catch {
      details = bodyText;
    }

    // Auto-refresh on 401: tenta renovar o token uma vez antes de desistir
    if (response.status === 401 && !options._retryCount && !options.skipAuthHandling) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        return request(path, { ...options, _retryCount: 1 });
      }
    }

    // Check for USER_DISABLED error code and trigger handler
    if (typeof details === 'object' && details !== null && 'code' in details) {
      const errorDetails = details as { code?: string; error?: string };
      if (errorDetails.code === 'USER_DISABLED' && userDisabledHandler) {
        userDisabledHandler(errorDetails.error ?? 'Your account has been disabled');
      }
    }

    // Check for 401 Unauthorized status (after auto-refresh attempt)
    if (response.status === 401 && unauthorizedHandler && !options.skipAuthHandling) {
      unauthorizedHandler();
    }

    const errorMessage = typeof details === 'object' && details !== null
      ? (details as any).message || (details as any).error || 'API Request Failed'
      : (details as string) || response.statusText;

    const errorCode = typeof details === 'object' && details !== null
      ? (details as any).code || (details as any).error
      : undefined;

    // Pass full details to ApiError
    throw new ApiError(errorMessage, errorCode, details, response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }
  return (await response.text()) as unknown as T;
}

// Handler for when user is disabled
type UserDisabledHandler = (message: string) => void;
let userDisabledHandler: UserDisabledHandler | null = null;

export function setUserDisabledHandler(handler: UserDisabledHandler | null) {
  userDisabledHandler = handler;
}

// Handler for when unauthorized (401)
type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  unauthorizedHandler = handler;
}

// Refresh lock to prevent concurrent refresh attempts (which would revoke the token family)
let refreshPromise: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${DEFAULT_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export const api = {
  async login(payload: LoginPayload) {
    const body: Record<string, string> = { username: payload.username, password: payload.password };
    if (payload.deviceId) {
      body.device_id = payload.deviceId;
    }
    const response = await request<any>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
      skipTenantHeader: true,
    });

    // Map from API response.
    // webLogin returns { token, sessionId, user } (camelCase)
    // apiLogin returns { access_token, session_id } (snake_case)
    // Since we are using /auth/login (webLogin), we should use the camelCase props if snake_case are missing.
    return {
        token: response.token || response.access_token,
        user: {
            ...response.user
        },
        sessionId: response.sessionId || response.session_id,
    } as AuthResponse;
  },

  /**
   * Solicita recuperação de senha
   * Envia email com link de reset para o usuário
   */
  async forgotPassword(email: string, options?: { redirectUrl?: string; appName?: string }): Promise<{ message: string }> {
    const fallbackRedirectUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/reset-password`
      : undefined;

    return request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({
        email,
        ...(options?.appName ? { app_name: options.appName } : {}),
        ...(options?.redirectUrl || fallbackRedirectUrl
          ? { redirect_url: options?.redirectUrl || fallbackRedirectUrl }
          : {}),
      }),
      skipTenantHeader: true,
    });
  },

  /**
   * Redefine a senha do usuário
   * Utiliza o token recebido por email para validar a operação
   */
  async resetPassword(token: string | null, newPassword: string): Promise<{ message: string }> {
    return request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
      skipTenantHeader: true,
    });
  },

  async logout(token: string | null, sessionId?: string) {
    try {
      await request('/api/v1/auth/logout', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId
        }),
        token,
        skipAuthHandling: true,
      });
    } catch (e) {
      console.warn('Logout API call failed', e);
    }
  },

  async getCurrentUser(token: string | null, options: { skipAuthHandling?: boolean } = {}) {
    const result = await request<{ user: UserAccount }>('/auth/me', {
      token,
      skipAuthHandling: options.skipAuthHandling,
    });
    return result.user;
  },

  async listDealers(token: string | null) {
    const result = await request<{ data: Dealer[] }>('/api/v1/dealers', { token });
    return result.data;
  },

  async createDealer(token: string | null, payload: CreateDealerPayload) {
    const body = {
      name: payload.name,
      code: payload.code,
      defaultLocale: payload.defaultLocale,
      ...(typeof payload.logoData !== 'undefined' ? { logoData: payload.logoData } : {}),
      ...(typeof payload.tenantLimit !== 'undefined' ? { tenantLimit: payload.tenantLimit } : {}),
      adminUser: payload.adminUser,
      applicationModules: payload.applicationModules,
      moduleExpirations: payload.moduleExpirations,
      licenseValidUntil: payload.licenseValidUntil,
    };
    const result = await request<{ data: { dealer: Dealer; adminUser: Record<string, unknown> } }>('/api/v1/dealers', {
      method: 'POST',
      body: JSON.stringify(body),
      token,
    });
    return result.data;
  },

  async updateDealer(token: string | null, id: string, payload: UpdateDealerPayload) {
    const body = {
      name: payload.name,
      ...(payload.defaultLocale ? { defaultLocale: payload.defaultLocale } : {}),
      ...(typeof payload.logoData !== 'undefined' ? { logoData: payload.logoData } : {}),
      ...(typeof payload.tenantLimit !== 'undefined' ? { tenantLimit: payload.tenantLimit } : {}),
      applicationModules: payload.applicationModules,
      moduleExpirations: payload.moduleExpirations,
      licenseValidUntil: payload.licenseValidUntil,
    };
    const result = await request<{ data: Dealer }>(`/api/v1/dealers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
      token,
    });
    return result.data;
  },

  async deleteDealer(token: string | null, id: string) {
    await request(`/api/v1/dealers/${id}`, {
      method: 'DELETE',
      token,
    });
  },

  async listApplications(token: string | null, params?: { dealerId?: string; includeModules?: boolean }) {
    const query = new URLSearchParams();
    if (params?.dealerId) {
      query.set('dealerId', params.dealerId);
    }
    if (params?.includeModules) {
      query.set('includeModules', 'true');
    }
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const result = await request<{ data: Application[] }>(`/api/v1/applications${suffix}`, { token });
    return result.data;
  },

  async createApplication(token: string | null, payload: CreateApplicationPayload) {
    const { dealerIds: _dealerIds, ...rest } = payload;
    const body = {
      name: rest.name,
      ...(rest.code ? { code: rest.code } : {}),
      ...(rest.description ? { description: rest.description } : {}),
      ...(rest.baseUrl ? { baseUrl: rest.baseUrl } : {}),
      ...(typeof rest.logoData !== 'undefined' ? { logoData: rest.logoData } : {}),
    };
    const result = await request<{ data: Application }>('/api/v1/applications', {
      method: 'POST',
      body: JSON.stringify(body),
      token,
    });
    return result.data;
  },

  async updateApplication(token: string | null, id: string, payload: UpdateApplicationPayload) {
    const { dealerIds: _dealerIds, ...rest } = payload;
    const body = {
      name: rest.name,
      ...(rest.code ? { code: rest.code } : {}),
      ...(rest.description ? { description: rest.description } : {}),
      ...(rest.baseUrl ? { baseUrl: rest.baseUrl } : {}),
      ...(typeof rest.logoData !== 'undefined' ? { logoData: rest.logoData } : {}),
    };
    const result = await request<{ data: Application }>(`/api/v1/applications/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
      token,
    });
    return result.data;
  },

  async deleteApplication(token: string | null, id: string) {
    await request(`/api/v1/applications/${id}`, {
      method: 'DELETE',
      token,
    });
  },

  async setApplicationDealers(token: string | null, id: string, payload: ApplicationAssignmentPayload) {
    const body = {
      dealerIds: payload.dealerIds ?? [],
    };
    const result = await request<{ data: Application }>(`/api/v1/applications/${id}/dealers`, {
      method: 'PUT',
      body: JSON.stringify(body),
      token,
    });
    return result.data;
  },

  async listApplicationModules(token: string | null, applicationId: string) {
    const result = await request<{ data: ApplicationModule[] }>(`/api/v1/applications/${applicationId}/modules`, {
      token,
    });
    return result.data;
  },

  async createApplicationModule(
    token: string | null,
    applicationId: string,
    payload: CreateApplicationModulePayload
  ) {
    const body = {
      code: payload.code,
      name: payload.name,
      ...(payload.description ? { description: payload.description } : {}),
      ...(payload.color ? { color: payload.color } : {}),
      ...(Array.isArray(payload.dealerIds) ? { dealerIds: payload.dealerIds } : {}),
    };
    const result = await request<{ data: ApplicationModule }>(`/api/v1/applications/${applicationId}/modules`, {
      method: 'POST',
      body: JSON.stringify(body),
      token,
    });
    return result.data;
  },

  async updateApplicationModule(
    token: string | null,
    applicationId: string,
    moduleId: string,
    payload: UpdateApplicationModulePayload
  ) {
    const body = {
      name: payload.name,
      ...(payload.code ? { code: payload.code } : {}),
      ...(payload.description ? { description: payload.description } : {}),
      ...(payload.color ? { color: payload.color } : {}),
      ...(Array.isArray(payload.dealerIds) ? { dealerIds: payload.dealerIds } : {}),
    };
    const result = await request<{ data: ApplicationModule }>(
      `/api/v1/applications/${applicationId}/modules/${moduleId}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
        token,
      }
    );
    return result.data;
  },

  async deleteApplicationModule(token: string | null, applicationId: string, moduleId: string) {
    await request(`/api/v1/applications/${applicationId}/modules/${moduleId}`, {
      method: 'DELETE',
      token,
    });
  },

  async setApplicationModuleDealers(
    token: string | null,
    applicationId: string,
    moduleId: string,
    payload: ModuleAssignmentPayload
  ) {
    const body = {
      dealerIds: payload.dealerIds ?? [],
    };
    const result = await request<{ data: ApplicationModule }>(
      `/api/v1/applications/${applicationId}/modules/${moduleId}/dealers`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
        token,
      }
    );
    return result.data;
  },

  async listTenants(token: string | null, dealerId?: string | null, options?: { includeDetails?: boolean }) {
    const query = new URLSearchParams();
    if (dealerId) {
      query.set('dealerId', dealerId);
    }
    if (options?.includeDetails === true) {
      query.set('includeDetails', 'true');
    }
    const suffix = query.toString() ? `?${query}` : '';
    const result = await request<{ data: Tenant[] }>(`/api/v1/tenants${suffix}`, { token });
    return result.data;
  },

  async createTenant(token: string | null, payload: CreateTenantPayload) {
    const body = {
      dealerId: payload.dealerId,
      name: payload.name,
      code: payload.code,
      status: payload.status,
      ...(payload.locale ? { locale: payload.locale } : {}),
      ...(typeof payload.logoData !== 'undefined' ? { logoData: payload.logoData } : {}),
      licenseValidUntil: payload.licenseValidUntil,
      maxPeople: payload.maxPeople,
      maxDevices: payload.maxDevices,
      isDemo: payload.isDemo ?? false,
      ...(Array.isArray(payload.applicationIds) ? { applicationIds: payload.applicationIds } : {}),
      ...(Object.prototype.hasOwnProperty.call(payload, 'applicationModules')
        ? { applicationModules: payload.applicationModules ?? {} }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(payload, 'moduleExpirations')
        ? { moduleExpirations: payload.moduleExpirations ?? {} }
        : {}),
      masterUser: payload.masterUser,
    };
    const result = await request<{ data: { tenant: Tenant; masterUser: Record<string, unknown> } }>(
      '/api/v1/tenants',
      {
        method: 'POST',
        body: JSON.stringify(body),
        token,
      }
    );
    return result.data;
  },

  async updateTenant(token: string | null, tenantId: string, payload: UpdateTenantPayload) {
    const body = {
      name: payload.name,
      status: payload.status,
      licenseValidUntil: payload.licenseValidUntil,
      maxPeople: payload.maxPeople,
      maxDevices: payload.maxDevices,
      ...(typeof payload.isDemo !== 'undefined' ? { isDemo: payload.isDemo } : {}),
      ...(payload.locale ? { locale: payload.locale } : {}),
      ...(typeof payload.logoData !== 'undefined' ? { logoData: payload.logoData } : {}),
      ...(Array.isArray(payload.applicationIds) ? { applicationIds: payload.applicationIds } : {}),
      ...(Object.prototype.hasOwnProperty.call(payload, 'applicationModules')
        ? { applicationModules: payload.applicationModules ?? {} }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(payload, 'moduleExpirations')
        ? { moduleExpirations: payload.moduleExpirations ?? {} }
        : {}),
    };
    const result = await request<{ data: Tenant }>(`/api/v1/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
      token,
    });
    return result.data;
  },

  async revokeTenant(token: string | null, tenantId: string, reason?: string) {
    const result = await request<{ data: Tenant }>(`/api/v1/tenants/${tenantId}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
      token,
    });
    return result.data;
  },

  async reactivateTenant(token: string | null, tenantId: string) {
    const result = await request<{ data: Tenant }>(`/api/v1/tenants/${tenantId}/reactivate`, {
      method: 'POST',
      token,
    });
    return result.data;
  },

  async getTenantRevocationDetails(token: string | null, id: string): Promise<{ revokedAt: string; revokedByUsername: string; reason: string | null } | null> {
    const response = await request<{ data: { revokedAt: string; revokedByUsername: string; reason: string | null; found: boolean } | null }>(
      `/api/v1/tenants/${id}/revocation-details`,
      { token }
    );
    return response.data?.found ? response.data : null;
  },

  async exportTenantLicense(token: string | null, tenantId: string): Promise<{ blob: Blob; filename: string }> {
    const headers = new Headers({
      'Content-Type': 'application/json',
    });
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(`${DEFAULT_BASE_URL}/api/v1/tenants/${tenantId}/license/export`, {
      method: 'POST',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const details = await response.json().catch(() => ({}));
      throw new ApiError(details.message || 'Failed to export license', details.code, details);
    }

    const contentDisposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : `license_${tenantId}.zip`;
    const blob = await response.blob();

    return { blob, filename };
  },

  async listUsers(token: string | null, params?: { dealerId?: string; tenantId?: string; includeDetails?: boolean }) {
    const query = new URLSearchParams();
    if (params?.tenantId) {
      query.set('tenantId', params.tenantId);
    } else if (params?.dealerId) {
      query.set('dealerId', params.dealerId);
    }
    if (params?.includeDetails === true) {
      query.set('includeDetails', 'true');
    } else if (params?.includeDetails === false) {
      query.set('includeDetails', 'false');
    }
    const suffix = query.toString() ? `?${query}` : '';
    const result = await request<{ data: AdminUser[] }>(`/api/v1/users${suffix}`, { token });
    return result.data;
  },

  async createUser(token: string | null, payload: CreateAdminUserPayload) {
    const result = await request<{ data: AdminUser }>('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify(payload),
      token,
    });
    return result.data;
  },

  async updateUser(token: string | null, userId: string, payload: UpdateAdminUserPayload) {
    const result = await request<{ data: AdminUser }>(`/api/v1/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      token,
    });
    return result.data;
  },

  async resetUserPassword(token: string | null, userId: string, newPassword: string, mustChange = true) {
    const result = await request<{ data: AdminUser }>(`/api/v1/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword, mustChangePassword: mustChange }),
      token,
    });
    return result.data;
  },

  async changePassword(token: string | null, payload: ChangePasswordPayload) {
    return request<AuthResponse>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(payload),
      token,
    });
  },

  async deleteUser(token: string | null, userId: string) {
    await request(`/api/v1/users/${userId}`, {
      method: 'DELETE',
      token,
    });
  },

  async getDashboardMetrics(token: string | null) {
    const result = await request<{ data: DashboardMetrics }>('/api/v1/admin/metrics/dashboard', { token });
    return result.data;
  },

  async getBusinessMetrics(token: string | null) {
    const result = await request<{ data: BusinessMetrics }>('/api/v1/admin/metrics/business', { token });
    return result.data;
  },

  async getMasterDashboard(token: string | null, options?: { includeDetails?: boolean }) {
    const query = new URLSearchParams();
    if (options?.includeDetails === true) {
      query.set('includeDetails', 'true');
    } else if (options?.includeDetails === false) {
      query.set('includeDetails', 'false');
    }

    const suffix = query.toString() ? `?${query.toString()}` : '';
    const result = await request<{ data: MasterDashboard }>(`/api/v1/admin/metrics/master-dashboard${suffix}`, { token });
    return result.data;
  },

  async listAuditLogs(token: string | null, params?: Record<string, string>) {
    const query = new URLSearchParams(params);
    const result = await request<{ data: any[] }>(`/api/v1/audit-logs?${query.toString()}`, { token });
    return result.data;
  },



  // Tickets
  async listTickets(token: string | null, params?: Record<string, string>) {
    const query = params ? new URLSearchParams(params) : '';
    const result = await request<{ data: SupportTicket[] }>(`/api/v1/tickets?${query}`, { token });
    return result.data;
  },

  async getTicket(token: string | null, id: string) {
    const result = await request<{ data: SupportTicket }>(`/api/v1/tickets/${id}`, { token });
    return result.data;
  },

  async createTicket(token: string | null, payload: CreateTicketPayload) {
    const result = await request<{ data: SupportTicket }>('/api/v1/tickets', {
      method: 'POST',
      body: JSON.stringify(payload),
      token,
    });
    return result.data;
  },

  async updateTicket(token: string | null, id: string, payload: UpdateTicketPayload) {
    const result = await request<{ data: SupportTicket }>(`/api/v1/tickets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      token,
    });
    return result.data;
  },

  async deleteTicket(token: string | null, id: string) {
    await request(`/api/v1/tickets/${id}`, {
      method: 'DELETE',
      token,
    });
  },

  async getTicketMessages(token: string | null, ticketId: string) {
    const result = await request<{ data: TicketMessage[] }>(`/api/v1/tickets/${ticketId}/messages`, { token });
    return result.data;
  },

  async addTicketMessage(token: string | null, ticketId: string, message: string, isInternal = false) {
    const result = await request<{ data: TicketMessage }>(`/api/v1/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message, isInternal }),
      token,
    });
    return result.data;
  },

  async getTicketStats(token: string | null) {
    const result = await request<{ data: TicketStats }>('/api/v1/tickets/stats', { token });
    return result.data;
  },

  // FAQ
  async listFaqs(token: string | null, params?: Record<string, string>) {
    const query = params ? new URLSearchParams(params) : '';
    const result = await request<{ data: FaqItem[] }>(`/api/v1/faq?${query}`, { token });
    return result.data;
  },

  async getFaq(token: string | null, id: string) {
    const result = await request<{ data: FaqItem }>(`/api/v1/faq/${id}`, { token });
    return result.data;
  },

  async createFaq(token: string | null, payload: CreateFaqPayload) {
    const result = await request<{ data: FaqItem }>('/api/v1/faq', {
      method: 'POST',
      body: JSON.stringify(payload),
      token,
    });
    return result.data;
  },

  async updateFaq(token: string | null, id: string, payload: UpdateFaqPayload) {
    const result = await request<{ data: FaqItem }>(`/api/v1/faq/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      token,
    });
    return result.data;
  },

  async deleteFaq(token: string | null, id: string) {
    await request(`/api/v1/faq/${id}`, {
      method: 'DELETE',
      token,
    });
  },

  async getFaqCategories(token: string | null) {
    const result = await request<{ data: string[] }>('/api/v1/faq/categories', { token });
    return result.data;
  },

  // Groups
  async listGroups(token: string | null) {
    const result = await request<{ data: Group[] }>('/api/v1/groups', { token });
    return result.data;
  },

  async getGroup(token: string | null, id: string) {
    const result = await request<{ data: Group }>(`/api/v1/groups/${id}`, { token });
    return result.data;
  },

  async createGroup(token: string | null, payload: CreateGroupPayload) {
    const result = await request<{ data: Group }>('/api/v1/groups', {
      method: 'POST',
      body: JSON.stringify(payload),
      token,
    });
    return result.data;
  },

  async updateGroup(token: string | null, id: string, payload: UpdateGroupPayload) {
    const result = await request<{ data: Group }>(`/api/v1/groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      token,
    });
    return result.data;
  },

  async deleteGroup(token: string | null, id: string) {
    await request(`/api/v1/groups/${id}`, {
      method: 'DELETE',
      token,
    });
  },

  // Notifications
  async listNotifications(token: string | null) {
    return request<{ data: Notification[] }>('/api/v1/notifications', { token });
  },

  async getNotificationCount(token: string | null) {
    return request<{ data: { count: number } }>('/api/v1/notifications/count', { token });
  },

  async markNotificationRead(token: string | null, notificationId: string) {
    return request('/api/v1/notifications/' + notificationId + '/read', {
      method: 'PUT',
      token,
    });
  },

  async markAllNotificationsRead(token: string | null) {
    return request('/api/v1/notifications/read-all', {
      method: 'PUT',
      token,
    });
  },

  // Onboarding - Unified creation of Dealer/Tenant/User
  async createOnboarding(token: string | null, payload: {
    company: {
      name: string;
      code: string;
      locale?: string;
      logoData?: string | null;
      phone?: string | null;
      contactEmail?: string | null;
    };
    license: {
      validUntil: string;
      maxPeople: number;
      maxDevices: number;
      isDemo?: boolean;
      applicationModules: Record<string, string[]>;
      moduleExpirations?: Record<string, string>;
    };
    admin: {
      username: string;
      email: string;
      displayName: string;
      password?: string;
    };
    dealerId?: string | null;
  }) {
    const result = await request<{
      success: boolean;
      message: string;
      data: {
        dealer: { id: string; name: string; code: string; isNew: boolean };
        tenant: { id: string; name: string; code: string; licenseValidUntil: string; maxPeople: number; maxDevices: number; isDemo: boolean };
        admin: { id: string; username: string; email: string; displayName: string; role: string; mustChangePassword: boolean; initialPassword?: string };
      };
    }>('/api/v1/onboarding', {
      method: 'POST',
      body: JSON.stringify(payload),
      token,
    });
    return result;
  },

  // Sessões
  async listActiveSessions(token: string | null, tenantId?: string) {
    const query = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const result = await request<{ id: string; userId: string; clientId: string | null; clientName?: string; lastActivity: number; createdAt: number; expiresAt: number; user?: any }[]>(`/api/v1/sessions${query}`, { token });
    return result;
  },

  async listRevokedSessions(token: string | null, tenantId?: string) {
    const query = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const result = await request<any[]>(`/api/v1/sessions/revoked${query}`, { token });
    return result;
  },

  async getSessionRevocationPolicy(token: string | null) {
    const result = await request<{ data: { type: string; defaultDurationMinutes: number; maxDurationMinutes: number; supportsPermanentRevocation: boolean; permanentRevocationValue: null | number } }>('/api/v1/sessions/revocation-policy', { token });
    return result.data;
  },

  async revokeActiveSession(token: string | null, sessionId: string, options?: { reason?: string; durationMinutes?: number | null }) {
    await request(`/api/v1/sessions/${sessionId}`, {
      method: "DELETE",
      token,
      body: JSON.stringify({
        reason: options?.reason || 'MANUAL_REVOCATION',
        durationMinutes: options?.durationMinutes,
      }),
    });
  },

  async unrevokeSession(token: string | null, sessionId: string) {
    await request(`/api/v1/sessions/${sessionId}/unrevoke`, {
      method: "POST",
      token,
    });
  },

  async reactivateSessionUser(token: string | null, userId: string) {
    return request<{ success: boolean; message: string; data: any }>(`/api/v1/sessions/${userId}/reactivate`, {
      method: 'POST',
      token,
    });
  },

  // Devices
  async listDevices(token: string | null, includeRevoked = false, options?: { includeDetails?: boolean }) {
    const queryParams = new URLSearchParams();
    if (includeRevoked) {
      queryParams.set('includeRevoked', 'true');
    }
    if (options?.includeDetails === true) {
      queryParams.set('includeDetails', 'true');
    }

    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const result = await request<{ data: Device[]; stats: DeviceStats }>(`/api/v1/devices${query}`, { token });
    return result;
  },

  async getDeviceRevocationPolicy(token: string | null) {
    const result = await request<{ data: { type: string; defaultDurationMinutes: number; maxDurationMinutes: number; supportsPermanentRevocation: boolean; permanentRevocationValue: null | number } }>('/api/v1/devices/revocation-policy', { token });
    return result.data;
  },

  async revokeDevice(token: string | null, id: string, options?: { reason?: string; durationMinutes?: number | null }) {
    await request(`/api/v1/devices/${id}`, {
      method: 'DELETE',
      token,
      body: JSON.stringify({
        reason: options?.reason || 'DEVICE_MANUAL_REVOCATION',
        durationMinutes: options?.durationMinutes,
      }),
    });
  },

  async unrevokeDevice(token: string | null, id: string) {
    await request(`/api/v1/devices/${id}/unrevoke`, {
      method: 'POST',
      token,
    });
  },

  async updateDeviceName(token: string | null, id: string, deviceName: string) {
    await request(`/api/v1/devices/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ deviceName }),
    });
  },

  // Health Dashboard
  async getSystemHealth(token: string | null) {
    return request<SystemHealth>('/api/v1/admin/health/system', { token });
  },
};

export type ApiClient = typeof api;

export default api;
